import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Bell,
  CheckCircle2,
  LogIn,
  LogOut,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  Server,
} from 'lucide-react';
import {
  useDashboardActivity,
  useDashboardErrors,
  useDashboardHealthStrip,
} from '@/api/hooks/use-dashboard';
import { usePlatformHealth } from '@/api/hooks/use-platform';
import { useAuth, useIsPlatformAdmin, useRole } from '@/providers/AuthProvider';
import { useTenant } from '@/providers/TenantProvider';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { formatRelativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { RecentActivityItem } from '@/types';

type NotificationSeverity = 'info' | 'warning' | 'error' | 'success';

type AppNotification = {
  id: string;
  title: string;
  detail?: string | null;
  occurredAt: string;
  href?: string;
  severity: NotificationSeverity;
};

const SEEN_KEY_PREFIX = 'voicera:notifications:seen-ids';
const MAX_SEEN_IDS = 120;

function seenStorageKey(userId: string, scope: string) {
  return `${SEEN_KEY_PREFIX}:${userId}:${scope}`;
}

function readSeenIds(userId: string, scope: string): Set<string> {
  try {
    const raw = localStorage.getItem(seenStorageKey(userId, scope));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === 'string'));
  } catch {
    return new Set();
  }
}

function writeSeenIds(userId: string, scope: string, ids: Set<string>) {
  try {
    const trimmed = [...ids].slice(-MAX_SEEN_IDS);
    localStorage.setItem(seenStorageKey(userId, scope), JSON.stringify(trimmed));
  } catch {
    /* ignore quota / private mode */
  }
}

function activityIcon(item: RecentActivityItem) {
  const detail = String(item.detail || '').toLowerCase();
  if (item.type === 'logout') return LogOut;
  if (item.type === 'login' || item.type === 'login_failed') return LogIn;
  if (item.type === 'call_outbound') return PhoneOutgoing;
  if (detail.includes('missed') || detail.includes('no answer')) return PhoneMissed;
  if (item.type === 'call_inbound') return PhoneIncoming;
  return Activity;
}

function activitySeverity(item: RecentActivityItem): NotificationSeverity {
  const detail = String(item.detail || '').toLowerCase();
  if (item.type === 'login_failed' || detail.includes('failed')) return 'error';
  if (detail.includes('missed') || detail.includes('no answer') || detail.includes('busy')) {
    return 'warning';
  }
  return 'info';
}

function severityStyles(severity: NotificationSeverity) {
  switch (severity) {
    case 'error':
      return 'bg-destructive/10 text-destructive';
    case 'warning':
      return 'bg-amber-500/10 text-amber-700';
    case 'success':
      return 'bg-success/10 text-success';
    default:
      return 'bg-primary/10 text-primary';
  }
}

function notificationIcon(item: AppNotification, activity?: RecentActivityItem) {
  if (activity) return activityIcon(activity);
  if (item.severity === 'error') return AlertCircle;
  if (item.severity === 'warning') return AlertTriangle;
  if (item.severity === 'success') return CheckCircle2;
  if (item.id.startsWith('platform:')) return Server;
  return Bell;
}

/** Header notification bell — active for all roles using existing operational data. */
export function NotificationBell() {
  const { session } = useAuth();
  const role = useRole();
  const isPlatformAdmin = useIsPlatformAdmin();
  const { hasTenant, tenantId } = useTenant();
  const userId = session?.user.id ?? 'anonymous';
  const scope =
    hasTenant && tenantId ? `tenant:${tenantId}` : isPlatformAdmin ? 'platform' : `role:${role}`;

  const [open, setOpen] = useState(false);
  const [seenIds, setSeenIds] = useState(() => readSeenIds(userId, scope));

  useEffect(() => {
    setSeenIds(readSeenIds(userId, scope));
  }, [scope, userId]);

  const { data: activity = [], isLoading: activityLoading } = useDashboardActivity();
  const { data: errors = [] } = useDashboardErrors();
  const { data: healthStrip = [] } = useDashboardHealthStrip();
  const { data: platformHealth, isLoading: platformHealthLoading } = usePlatformHealth({
    enabled: isPlatformAdmin && !hasTenant,
  });

  const activityById = useMemo(() => {
    const map = new Map<string, RecentActivityItem>();
    for (const item of activity) map.set(item.id, item);
    return map;
  }, [activity]);

  const notifications = useMemo(() => {
    const items: AppNotification[] = [];
    const nowIso = new Date().toISOString();

    if (hasTenant) {
      for (const err of errors) {
        items.push({
          id: `error:${err.id}`,
          title: err.message,
          detail: err.severity === 'error' ? 'Needs attention' : 'Warning',
          occurredAt: err.occurred_at,
          href: err.fix_target?.href,
          severity: err.severity === 'error' ? 'error' : 'warning',
        });
      }

      for (const health of healthStrip) {
        if (health.status === 'degraded' || health.status === 'outage') {
          items.push({
            id: `health:${health.id}:${health.status}`,
            title: `${health.label} is ${health.status}`,
            detail:
              health.latency_ms != null
                ? `${health.latency_ms}ms · Check Health`
                : 'Check system health',
            occurredAt: nowIso,
            href: '/health',
            severity: health.status === 'outage' ? 'error' : 'warning',
          });
        }
      }

      for (const item of activity) {
        items.push({
          id: item.id,
          title: item.message,
          detail: item.detail,
          occurredAt: item.occurred_at,
          href: item.href,
          severity: activitySeverity(item),
        });
      }
    } else if (isPlatformAdmin) {
      const status = String(platformHealth?.status || 'unknown').toLowerCase();
      if (status === 'ok' || status === 'operational') {
        items.push({
          id: 'platform:health:ok',
          title: 'Platform systems operational',
          detail: 'Postgres and platform services look healthy',
          occurredAt: nowIso,
          href: '/platform/health',
          severity: 'success',
        });
      } else if (platformHealth) {
        items.push({
          id: `platform:health:${status}`,
          title: `Platform health: ${status}`,
          detail: 'Review system health',
          occurredAt: nowIso,
          href: '/platform/health',
          severity: status === 'error' || status === 'outage' ? 'error' : 'warning',
        });
      }

      const services = platformHealth?.services ?? [];
      for (const service of services) {
        const name = String((service as { name?: string }).name || 'Service');
        const svcStatus = String((service as { status?: string }).status || 'unknown').toLowerCase();
        if (svcStatus !== 'ok' && svcStatus !== 'operational') {
          items.push({
            id: `platform:service:${name}`,
            title: `${name} is ${svcStatus}`,
            detail: 'Platform service alert',
            occurredAt: nowIso,
            href: '/platform/health',
            severity: svcStatus === 'error' ? 'error' : 'warning',
          });
        }
      }
    }

    items.sort((a, b) => {
      const ta = Date.parse(a.occurredAt) || 0;
      const tb = Date.parse(b.occurredAt) || 0;
      return tb - ta;
    });

    return items.slice(0, 25);
  }, [activity, errors, hasTenant, healthStrip, isPlatformAdmin, platformHealth]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !seenIds.has(n.id)).length,
    [notifications, seenIds]
  );

  const markSeen = useCallback(() => {
    setSeenIds((prev) => {
      const next = new Set(prev);
      for (const item of notifications) next.add(item.id);
      writeSeenIds(userId, scope, next);
      return next;
    });
  }, [notifications, scope, userId]);

  const isLoading = hasTenant
    ? activityLoading
    : isPlatformAdmin
      ? platformHealthLoading
      : false;

  const emptyHint = hasTenant
    ? role === 'USER'
      ? 'Your calls and sign-ins will show up here.'
      : 'Operational events and alerts will show up here.'
    : 'Platform health alerts will show up here.';

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        if (open && !next) markSeen();
        setOpen(next);
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={
            unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'
          }
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 ? (
            <span
              className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground"
              aria-hidden
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[22rem] p-0 sm:w-[24rem]">
        <div className="flex items-center justify-between px-3 py-2.5">
          <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
          {unreadCount > 0 ? (
            <span className="text-xs text-muted-foreground">{unreadCount} new</span>
          ) : (
            <span className="text-xs text-muted-foreground">Up to date</span>
          )}
        </div>
        <DropdownMenuSeparator className="m-0" />
        <ScrollArea className="h-[22rem]">
          {isLoading ? (
            <p className="px-3 py-8 text-center text-sm text-muted-foreground">
              Loading notifications…
            </p>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-3 py-10 text-center">
              <Bell className="h-8 w-8 text-muted-foreground/50" aria-hidden />
              <p className="text-sm font-medium">No notifications yet</p>
              <p className="text-xs text-muted-foreground">{emptyHint}</p>
            </div>
          ) : (
            <ul className="py-1">
              {notifications.map((item) => {
                const activityItem = activityById.get(item.id);
                const Icon = notificationIcon(item, activityItem);
                const isUnread = !seenIds.has(item.id);
                const body = (
                  <>
                    <span
                      className={cn(
                        'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                        severityStyles(item.severity)
                      )}
                    >
                      <Icon className="h-4 w-4" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm leading-snug">{item.title}</span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                        {item.detail ? (
                          <span className="capitalize">{item.detail}</span>
                        ) : null}
                        {item.detail ? <span aria-hidden>·</span> : null}
                        <time dateTime={item.occurredAt}>
                          {formatRelativeTime(item.occurredAt)}
                        </time>
                      </span>
                    </span>
                    {isUnread ? (
                      <span
                        className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary"
                        aria-label="Unread"
                      />
                    ) : (
                      <span className="mt-2 h-2 w-2 shrink-0" aria-hidden />
                    )}
                  </>
                );

                return (
                  <li key={item.id}>
                    {item.href ? (
                      <Link
                        to={item.href}
                        className="flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/80"
                        onClick={() => setOpen(false)}
                      >
                        {body}
                      </Link>
                    ) : (
                      <div className="flex w-full items-start gap-3 px-3 py-2.5">{body}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </ScrollArea>
        {hasTenant ? (
          <>
            <DropdownMenuSeparator className="m-0" />
            <div className="p-2">
              <Button asChild variant="ghost" size="sm" className="w-full justify-center">
                <Link to="/dashboard" onClick={() => setOpen(false)}>
                  Open dashboard
                </Link>
              </Button>
            </div>
          </>
        ) : isPlatformAdmin ? (
          <>
            <DropdownMenuSeparator className="m-0" />
            <div className="p-2">
              <Button asChild variant="ghost" size="sm" className="w-full justify-center">
                <Link to="/platform/health" onClick={() => setOpen(false)}>
                  System health
                </Link>
              </Button>
            </div>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
